import * as React from 'react';
import {Helmet} from 'react-helmet';

import {Link} from 'react-router-dom';

import {eventToLink} from '@app/components/LocalCommon/Utils/eventToLink';
import withSeo from '@app/components/LocalCommon/HigherOrderComponents/WithSeo';

export const MustHeadTitle = 'Veterinary Resources to Help You and Your Pet Families';

export const MustHeadText = 'We feel strongly about sharing information with our veterinary colleagues that will help pets have the peaceful end-of-life experience they deserve. We hope that sharing our tried-and-true practices with veterinary teams will create a dignified, supportive clinic environment that empowers team members to provide the best possible experience for everyone involved.';

const LinkList = ({list}) => {
	return (
		<ul>
			{
				list.map((item, index) => {
					const outerLink = /http*./;
					return (

						<li key={`${item.title}${index}`}>
							{
								outerLink.test(item.href) ?
									<a
										href={item.href}
										className="inline-link"
										target="_blank"
									>
										{item.title}
									</a>
									:
									<Link
										to={item.href}
										className="inline-link"
										target="_blank"
									>
										{item.title}
									</Link>
							}
							{item?.nextText}
						</li>
					);
				})
			}
		</ul>
	);
};

const SedationProtocolResource = () => {
	return (
		<>
			<Helmet>
				<title>Euthanasia Sedation Protocols | Lap of Love</title>
			</Helmet>
			<div className="resource-item">
				<h4>Pre-euthanasia Sedation</h4>
				<p>
					Pre-euthanasia sedation is helpful for many reasons. The pet remains calm and anxiety-free, and unwanted
					reactions are less likely. Also, the pet owner can watch their pet fall asleep gradually before the
					final injection. Every pet deserves pre-euthanasia sedation, and appropriate medications should be
					included in every euthanasia experience.
					<br/><br/>
					Here are sedation protocols that are helpful to our Lap of Love veterinarians:
				</p>
				<br/>
				<ul>
					<li className="resource-item__head-li">
						Dogs {'<'} 20 pounds (combine in syringe, administer SQ or IM):
						<ul>
							<li>Telazol: 0.1 ml/10 lb</li>
							<li>Ketamine: 0.1 ml/10 lb</li>
							<li>Acepromazine: 0.1-0.2 ml/10 lb</li>
						</ul>
					</li>
					<li className="resource-item__head-li">
						Dogs {'>'} 20 pounds (combine in syringe, administer SQ or IM):
						<ul>
							<li>Ketamine 0.1 ml/10 lb</li>
							<li>Xylazine (100 mg/ml) 0.05 ml/10 lb</li>
							<li>Acepromazine 0.1 ml/10 lb</li>
						</ul>
					</li>
					<li className="resource-item__head-li">
						Cats (combine in syringe, administer SQ or IM):
						<ul>
							<li>Telazol 0.2 ml</li>
							<li>Ketamine 0.3 ml</li>
							<li>Hub of acepromazine</li>
						</ul>
					</li>
				</ul>
				<ul>
					<li className="resource-item__head-li">
						Dogs (combine in syringe, administer SQ or IM):
						<ul>
							<li>Ketamine 0.1 ml/10 lb</li>
							<li>Xylazine (100 mg/ml) 0.05 ml/10 lb</li>
							<li>Acepromazine 0.1 ml/10 lb</li>
							<li>Butorphanol 0.05 ml/10 lb</li>
						</ul>
					</li>
				</ul>

				<h4>Resources</h4>

				<p>
					If you would like additional information about pre-euthanasia sedation, the article, <a
					href={'https://www.dvm360.com/view/how-can-you-improve-euthanasia-appointments-be-peacemaker'}
					className={'inline-link'} target="_blank">How can you improve
					euthanasia appointments? Be a peacemaker</a>, by Dr. Mary Gardner, published in dvm360, will be helpful.
				</p>
			</div>
		</>
	);
};

const BestPracticesResource = () => (
	<>
		<Helmet>
			<title>Euthanasia Best Practices | Lap of Love</title>
		</Helmet>
		<div className="resource-item">
			<h4>Euthanasia Best Practices</h4>
			<p>
				The euthanasia experience requires care, compassion, and confidence. It is the appointment that families
				dread—and remember—the most, so you must handle it well. We suggest you plan the entire experience, from
				the initial phone call to aftercare, to ensure every aspect is handled smoothly and compassionately. We
				have learned many tips and techniques along the way, and are honored to share them to help you create
				the best end-of-life experience possible for your patients and their families.
			</p>
			<h4>Scheduling euthanasia appointments</h4>
			<p>
				When scheduling a euthanasia appointment, your team member should focus completely on the client, giving
				them their full attention. Conveying empathy is critical, and saying, “I’m so sorry you are facing
				this,” can be helpful. The team member should also gather as much information as possible at this time,
				when clients generally are most capable of making difficult decisions. Emotions become more painful from
				here on out. Your support staff also should review aftercare options and pricing while making the
				appointment, and encourage clients to make the decision ahead of time, rather than during the euthanasia
				appointment.
			</p>
			<h4>Creating a welcoming euthanasia room</h4>
			<p>Ideally, you should designate a room specifically for euthanasia appointments, and make the room as
				welcoming, warm, and comfortable as possible. We suggest that you:
			</p>
			<ul>
				<li>Place a large, fluffy, rubber-backed bath rug on the ground or table</li>
				<li>Wrap the pet in soft, pretty blankets and towels</li>
				<li>Provide soft background noise, such as a water feature, or soft, wordless music</li>
				<li>Dim the lights or use a lamp, rather than turning on fluorescent lights</li>
				<li>
					Stock a basket with comfort items, such as:
					<ul>
						<li>Water bottles</li>
						<li>A small cloth bag for the pet’s collar</li>
						<li>A container for the pet’s hair clippings</li>
						<li>Material to make a paw print</li>
						<li>Tissues</li>
						<li>A small mirror, because women like to check their makeup after they have been crying</li>
					</ul>
				</li>
			</ul>
			<h4>Preparing the owner for euthanasia</h4>
			<p>
				You do not need to describe everything that may happen as a pet passes. We recommend saying only, “I
				would like to prepare you for two things—your pet’s eyes won’t close all the way, and their bladder may
				relax. If anything else happens, I’ll explain it at the time.”
			</p>

			<p>
				Using appropriate language to explain any post-mortem side effects to the pet owner is important. For
				example:
			</p>
			<ul>
				<li>
					<b>Agonal breathing </b>
					— “This is a spasm of the diaphragm, like a hiccup—it’s uncommon, but normal.”

				</li>
				<li>
					<b>Twitching </b>
					— “Death is a phase, not a moment, and this is normal as different areas of the body shut
					down.”
				</li>
				<li>
					<b>Stretching </b>
					— “This happens frequently when pets pass on their own, and tells me your pet was really
					close to the natural dying process.”
				</li>
			</ul>
			<h4>Making each euthanasia appointment the best possible experience</h4>
			<p>
				The three best things you can do during a euthanasia appointment are:
			</p>
			<ul>
				<li>Show love to the pet.</li>
				<li>Acknowledge the euthanasia decision by telling the family, “We are doing the best thing for them.”
				</li>
				<li>Physically touch the owner, which conveys more empathy than words ever will.</li>
			</ul>
			<p>
				We find that using comforting language can help soften the experience for pet owners, and confirm they
				are doing the best thing for their pet.
			</p>
			<table>
				<thead>
				<tr>
					<th>Instead of ...</th>
					<th>Try saying ...</th>
				</tr>
				</thead>
				<tbody>
				<tr>
					<td>You are doing the right thing.</td>
					<td>We are doing the best thing.</td>
				</tr>
				<tr>
					<td>Don’t worry about your pet.</td>
					<td>Your pet is in good hands.</td>
				</tr>
				<tr>
					<td>There is nothing more you can do.</td>
					<td>You have done an amazing job.</td>
				</tr>

				<tr>
					<td>Your pet is no longer in pain.</td>
					<td>Your pet feels so much better now.</td>
				</tr>
				<tr>
					<td>Is your pet good with strangers?</td>
					<td>Does your pet like meeting new friends? Do you think they would mind if I give them some love?
					</td>
				</tr>
				<tr>
					<td>Your other pet needs to leave.</td>
					<td>Let’s have your other pet wait in another room. I’d like your attention to be with Fluffy right
						now.
					</td>
				</tr>
				<tr>
					<td>Suffering</td>
					<td>Struggling</td>
				</tr>
				</tbody>
			</table>
			<h4>Collecting payment for a euthanasia</h4>
			<p>
				Pet owners, understandably, will be upset after their pet passes, so we recommend you collect payment in
				the room prior to euthanasia. This way, the owner will not have to stop at your front desk afterward.
			</p>
			<h4>After euthanasia</h4>
			<p>
				We recommend you provide each pet owner a memorial item, such as a paw or nose print, or lock of fur,
				during the appointment. A team member should call the owner the next day to check in, and let them know
				you are thinking of them. Be prepared to offer the owner resources, such as support groups and <Link
				to={'/our-services/pet-loss-support'} className="inline-link" target="_blank">pet loss
				support services</Link>, to help them deal with their grief.
			</p>
			<p>
				Your entire team must treat the pet’s body with dignity and respect as they prepare for their aftercare.
				Ensure you partner with a reliable, trustworthy aftercare company, and schedule annual visits to ensure
				they meet your highest standards. Know exactly what each aftercare option, including private,
				individual, and communal cremation, entails.
			</p>
			<h4>Resources</h4>
			<p>
				If you would like additional information about euthanasia, you may find the following articles, written
				by Lap of Love’s co-founders, helpful.
			</p>
			<ul>
				<li>
					<Link
						to="/veterinary-resources/handling-euthanasia-in-your-practice.pdf" className="inline-link"
						target="_blank" onClick={() => eventToLink('Handling Euthanasia in Your Practice')}>
						Handling Euthanasia in Your Practice
					</Link>
					, Drs. Mary Gardner and Dani McVety,
					Today’s
					Veterinary
					Practice, Practice-Building series
				</li>
				<li><Link
					to="/veterinary-resources/top-5-ways-to-honor-deceased-pets.pdf" className="inline-link"
					target="_blank" onClick={() => eventToLink('Top 5 Ways to Honor Deceased Pets')}>
					Top 5 Ways to Honor Deceased Pets</Link>, Dr. Dani McVety, Veterinary Team Brief
				</li>
				<li><Link
					to="/veterinary-resources/alternate-routes-for-euthanasia.pdf" className="inline-link"
					target="_blank" onClick={() => eventToLink('Cannot Find a Vein? Alternate Routes for Euthanasia')}>
					Cannot Find a Vein? Alternate Routes for Euthanasia</Link>, Dr. Mary Gardner,
					Veterinary Team Brief
				</li>
				<li><a
					href="https://www.dvm360.com/view/euthanasia-too-soon-not-soon-enough"
					className="inline-link"
					target="_blank"
				>Euthanasia: Too Soon? Not Soon Enough?</a>, Dr. Mary Gardner, dvm360</li>
			</ul>
			<h5>Lap of Love resources:</h5>
			<ul>
				<li>
					<Link
						to="/public/how-will-i-know-it-is-time/interactive-quality-of-life-assessment-and-daily-diary.pdf"
						className="inline-link"
						target="_blank"
						onClick={() => eventToLink('Quality of Life Daily Assessment')}
					>
						Quality of Life Daily Assessment
					</Link>
				</li>
				<li><Link
					to="/public/how-will-i-know-it-is-time/lap-of-love-quality-of-life-scale.pdf"
					className="inline-link"
					target="_blank"
					onClick={() => eventToLink('Lap of Love Quality of Life Scale')}>
					Lap of Love Quality of Life Scale</Link></li>
				<li>
					<Link
						to="/quality-of-life-calendar.pdf"
						className="inline-link"
						target="_blank"
						onClick={() => eventToLink('Quality of Life Calendar')}
					>
						Quality of Life Calendar
					</Link>
				</li>
				<li>
					<a
						href="https://www.pethospicejournal.com/homepage.aspx"
						className="inline-link"
						target="_blank"
					>
						Pet Hospice Journal
					</a>
					— web-based, interactive quality-of-life tool</li>
				<li>
					<Link
						to="/how-will-i-know-it-is-time"
						className="inline-link"
						target="_blank"
					>
						How Will I Know It’s Time?
					</Link>
				</li>
				<li>
					<Link
						to="/veterinary-resources/end-of-life-care-essentials-checklist.pdf"
						className="inline-link"
						target="_blank"
						onClick={() => eventToLink('End of Life Care Essentials Checklist')}
					>
						End of Life Care Essentials Checklist
					</Link>
				</li>
				<li>
					<Link
						to="/veterinary-resources/saying-goodbye-sign.jpg"
						className="inline-link"
						target="_blank"
						onClick={() => eventToLink('“Saying Goodbye” front desk sign')}
					>
						“Saying Goodbye” front desk sign
					</Link>
				</li>
			</ul>
			<h5>Additional helpful resources:</h5>
			<ul>
				<li>
					<a href="https://pawspice.com/q-of-l-care/new-page.html" className="inline-link" target="_blank">
						HHHHHMM quality of life scale
					</a>
				</li>
				<li>
					<a href="https://www.dvm360.com/view/reframing-discussion-convenience-euthanasia" className="inline-link" target="_blank">
						Reframing the Discussion of Convenience Euthanasia
					</a>
					, dvm360
				</li>
				<li>
					<a href="https://www.dvm360.com/view/veterinarians-you-put-comfort-comfort-room" className="inline-link" target="_blank">
						Veterinarians: You Put the ‘Comfort’ in ‘Comfort Room’
					</a>
					, dvm360
				</li>
				<li>
					<a href="https://www.dvm360.com/view/navigating-unusual-euthanasia-requests" className="inline-link" target="_blank">
						Navigating unusual euthanasia requests
					</a>
					, dvm360
				</li>
			</ul>
			<h5>Pet loss books for adults:</h5>
			<ul>
				<li>
					<a href="https://www.amazon.com/dp/080664348X/ref=cm_sw_su_dp" className="inline-link" target="_blank">
						Grieving the Death of a Pet
					</a>
					, Betty Carmack
				</li>
				<li>
					<a href="https://www.amazon.com/dp/1598584537/ref=cm_sw_su_dp" className="inline-link" target="_blank">
						Coping With Sorrow on the Loss of Your Pet
					</a>
					, Moira Anderson Allen
				</li>
				<li>
					<a href="https://www.amazon.com/dp/0813813271/ref=cm_sw_su_dp" className="inline-link" target="_blank">
						Pet Loss and Human Bereavement
					</a>
					, Williams Kay, et al.
				</li>
				<li>
					<a href="https://www.amazon.com/dp/0345421175/ref=cm_sw_su_dp" className="inline-link" target="_blank">
						Animals as Teachers and Healers: True Stories and Reflections
					</a>
					, Susan McElroy
				</li>
				<li>
					<a href="https://www.amazon.com/dp/B004J255BE/ref=cm_sw_su_dp" className="inline-link" target="_blank">
						A Final Act of Caring: Ending the Life of an Animal Friend
					</a>
					, Mary and Herb Montgomery
				</li>
				<li>
					<a href="https://www.amazon.com/dp/B003F76J64/ref=cm_sw_su_dp" className="inline-link" target="_blank">
						Pet Loss: A Thoughtful Guide for Adults and Children
					</a>
					, Herbert Neiburg
				</li>
				<li>
					<a href="https://www.amazon.com/dp/0965593614/ref=cm_sw_su_dp" className="inline-link" target="_blank">
						It’s OK to Cry: Warm Compassionate Stories That Will Help You Find Hope and Healing After the Death
					of a Beloved Pet
					</a>
					, Maria Quintana, et al.
				</li>
			</ul>
			<h5>Pet loss books for children: </h5>
			<ul>
				<li>
					<a
						href="https://www.amazon.com/dp/0698116666/ref=cm_sw_su_dp" className="inline-link"
						target="_blank">
						When a Pet Dies
					</a>
					, Fred Rogers
				</li>
				<li>
					<a
						href="https://www.amazon.com/dp/0590417010/ref=cm_sw_su_dp" className="inline-link"
						target="_blank">
						Dog Heaven
					</a>
					, Cynthia Rylant
				</li>
				<li>
					<a
						href="https://www.amazon.com/dp/0590100548/ref=cm_sw_su_dp" className="inline-link"
						target="_blank">
						Cat Heaven
					</a>
					, Cynthia Rylant
				</li>
			</ul>
		</div>
	</>
);

const PainManagementResource = () => {
	const ResourceListOne = [
		{
			title: 'Journal Scan: Studying the Caregiver Burden of Owners With Very Ill Pets',
			href: 'https://www.dvm360.com/view/journal-scan-studying-caregiver-burden-owners-with-very-ill-pets',
			nextText: ', Dr. Mary Gardner, dvm360'
		},
		{
			title: 'Is it Time for Euthanasia? Helping Owners Assess a Pet’s Quality of Life',
			href: 'https://www.dvm360.com/view/it-time-euthanasia-helping-owners-assess-pets-quality-life',
			nextText: ', Dr. Mary Gardner, dvm360'
		},
		{
			title: 'Helping clients face the caregiver\'s dilemma: How do I tend to a sick pet without losing myself?',
			href: 'https://www.dvm360.com/view/helping-clients-face-caregivers-dilemma-how-do-i-tend-sick-pet-without-losing-myself',
			nextText: ', Dr. Mary Gardner, dvm360'
		},
		{
			title: '4 products I love for old pets',
			href: 'https://www.dvm360.com/view/4-products-i-love-old-pets',
			nextText: ', Dr. Mary Gardner, dvm360'
		},
		{
			title: 'Pain Management in an Arthritic Senior Cat',
			href: 'https://files.brief.vet/migration/article/42611/cip_pain-management-in-an-arthritic-senior-cat-42611-article.pdf',
			nextText: ', Dr. Sheilah Robertson, Clinician’s Brief'
		},
		{
			title: '3 Updates in Feline Anesthesia and Analgesia',
			href: 'https://www.dvm360.com/view/3-updates-feline-anesthesia-and-analgesia',
			nextText: ', Dr. Sheliah Robertson, dvm360'
		}
	];

	const LoLResources = [
		{
			title: 'NSAID release form',
			href: '/veterinary-resources/nsaid-release.pdf'
		},
		{
			title: 'Hospice Consultation Consent Form',
			href: '/veterinary-resources/consent-for-consultation.pdf'
		},
		{
			title: 'Hospice Consult History and Family Concerns Form',
			href: '/veterinary-resources/history-and-family-concerns-consult.pdf'
		},
		{
			title: 'General Nursing Care for the Hospice Patient',
			href: '/veterinary-resources/nursing-care-for-hospice-clients.pdf'
		},
		{
			title: 'Quality of Life Daily Assessment',
			href: '/public/how-will-i-know-it-is-time/interactive-quality-of-life-assessment-and-daily-diary.pdf'
		},
		{
			title: 'Lap of Love Quality of Life Scale',
			href: '/public/how-will-i-know-it-is-time/lap-of-love-quality-of-life-scale.pdf'
		},
		{
			title: 'Quality of Life Calendar',
			href: '/quality-of-life-calendar.pdf'
		},
		{
			title: 'Pet Hospice Journal ',
			nextText: '— Web-based, interactive quality-of-life tool',
			href: 'https://www.pethospicejournal.com/homepage.aspx'
		},
		{
			title: 'End of Life Care Essentials Checklist',
			href: '/veterinary-resources/end-of-life-care-essentials-checklist.pdf'
		}
	];

	const AdditionalResources = [
		{
			title: 'Demystifying Feline Pain Management',
			href: 'https://www.dvm360.com/view/demystifying-feline-pain-management',
			nextText: ', dvm360'
		},
		{
			title: 'Canine and feline Pain Management: Expert Insight into Practitioners’ Top Questions',
			href: '/veterinary-resources/peer-reviewed-pain-management-faq.pdf',
			nextText: ', Today’s Veterinary Practice'
		},
		{
			title: 'Quality of Life and Death',
			href: 'https://www.dvm360.com/view/quality-life-and-death',
			nextText: ', dvm360'
		},
		{
			title: 'HHHHHMM quality of life scale',
			href: 'https://pawspice.com/q-of-l-care/new-page.html',
			nextText: ''
		},
		{
			title: '2018 AAFP Feline Anesthesia Guidelines',
			href: 'https://catvets.com/guidelines/practice-guidelines/anesthesia-guidelines',
			nextText: ''
		},
		{
			title: '2022 AAHA/AAFP Pain Management Guidelines for Dogs and Cats',
			href: 'https://www.aaha.org/aaha-guidelines/pain-management-config/pain-management-intro/',
			nextText: ''
		},
	];

	return (
		<>
			<Helmet>
				<title>Hospice Pain Management | Lap of Love</title>
			</Helmet>
			<div className="resource-item">
			<h4>Hospice Care Tips</h4>
			<p>
				Hospice is a medically supervised service dedicated to providing comfort and quality of life for a pet,
				and support for their owners, until euthanasia is elected or natural death occurs. Veterinary hospice
				care helps pets live their last days in comfort, allows pets and their owners more time together, and
				helps them create good memories of their final days together. Your practice likely already offers some
				degree of hospice care, whether or not it is labeled as such, but creating a comprehensive hospice care
				program will allow you to support more pets and their families.
			</p>
			<h4>Pain management for hospice patients</h4>
			<p>
				Many geriatric pets experience some form of pain, so pain management is a critical part of hospice care.
				Each pet will require an individualized pain management plan because their situation is different. Pet
				owners should be made aware that their pet’s plan may change as their condition progresses, and they
				must monitor their pet for signs that indicate additional pain control would be beneficial.
			</p>

			<p>
				Following are general pain management protocols and guidelines used by Lap of Love veterinarians.
			</p>
			<h4>Pain Management Protocols for Geriatric Dogs</h4>
			<table>
				<thead>
				<tr>
					<th>Medication</th>
					<th>Dose</th>
					<th>Helpful tips</th>
				</tr>
				</thead>
				<tbody>
				<tr>
					<td>NSAIDs</td>
					<td>Use the lowest effective dose, which may be as low as 1/2 the recommended dose</td>
					<td>Ensure the pet has eaten before administering</td>
				</tr>
				<tr>
					<td>Gabapentin</td>
					<td>5-10 mg/kg TID or QID</td>
					<td>Start at 5 mg/kg and increase the dose as needed</td>
				</tr>
				<tr>
					<td>Amantadine</td>
					<td>3 mg/kg SID</td>
					<td>Amantadine is an NMDA antagonist, and is useful for chronic pain
						Adverse reactions include panting and diarrhea
					</td>
				</tr>
				<tr>
					<td>Acetaminophen</td>
					<td>10 mg/kg BID or TID</td>
					<td>Can be used with NSAIDs
						Useful for treating breakthrough pain
					</td>
				</tr>
				</tbody>
			</table>
			<p>
				*We do not recommend using <Link to="/veterinary-resources/tramadol-in-dogs.pdf" className="inline-link"
				target="_blank" onClick={() => eventToLink('Tramadol for analgesia in dogs')}>Tramadol for analgesia in dogs</Link>, as it has poor
				bioavailability when
				administered orally or rectally
			</p>
			<h4>
				Pain Management Protocols for Geriatric Cats
			</h4>
			<table>
				<thead>
				<tr>
					<th>Medication</th>
					<th>Dose</th>
					<th>Helpful tips</th>
				</tr>

				</thead>
				<tbody>
				<tr>
					<td>Metacam</td>
					<td>0.03 mg/kg SID</td>
					<td>May help with nephritis in cats with mild renal failure</td>
				</tr>
				<tr>
					<td>Gabapentin</td>
					<td>12.5 mg per cat BID</td>
					<td>Use compounded formulation</td>
				</tr>
				<tr>
					<td>Buprenorphine</td>
					<td>0.02 mg/kg SL or TD</td>
					<td>{''}</td>
				</tr>
				<tr>
					<td>Tramadol</td>
					<td>Start with 6.25 mg/kg BID</td>
					<td>{''}</td>
				</tr>
				</tbody>
			</table>
			<h4>
				Anxiety management for hospice patients
			</h4>
			<p>
				Many geriatric patients experience increased anxiety, particularly if they have cognitive dysfunction.
				Environmental modification, such as adding nightlights, playing a sound machine at night, and gating off
				stairs, can help with anxiety management. Anxiolytic medications may also be helpful.
			</p>
			<h4>Anxiety Management Protocols</h4>
			<table>
				<thead>
				<tr>
					<th>Medication</th>
					<th>Dose</th>
					<th>Helpful tips</th>
				</tr>
				</thead>
				<tbody>
				<tr>
					<td>Amitriptyline</td>
					<td>Dogs: 1-2 mg/kg BID
						Cats: 2.5 mg per cat BID
					</td>
					<td>Amitriptyline is a tricyclic antidepressant, which increases serotonin
						Risks with tramadol, selegiline, and SAMe
					</td>
				</tr>
				<tr>
					<td>Alprazolam (Xanax)</td>
					<td>Dogs: 0.02-0.05 mg/kg q4-6 hr
						Cats: 0.125-0.25 mg per cat SID or BID
					</td>
					<td>May help with separation anxiety
						Consult your lawyer for local regulations and laws regarding legal documentation
					</td>
				</tr>
				</tbody>
			</table>
			<h4>Resources</h4>
			<h5>
				If you would like additional information about hospice care, you may find the following articles,
				written by our Lap of Love veterinarians, helpful.
			</h5>
			<LinkList list={ResourceListOne}/>

			<h5>Lap of Love resources</h5>
			<LinkList list={LoLResources}/>

			<h5>Additional resources that may be helpful:</h5>
			<LinkList list={AdditionalResources}/>

		</div>
		</>
	);
};

const GeriatricClinicTips = () => {
	const ResourceListOne = [
		{
			title: 'Caring for the Geriatric Pet',
			href: 'https://www.veterinarypracticenews.com/caring-for-the-geriatric-pet/',
			nextText: ', Dr. Mary Gardner, Veterinary Practice News, Geriatric Pet series, Part I'
		},
		{
			title: 'Supporting the Aging Pet',
			href: 'https://www.veterinarypracticenews.com/publications/de/201807/index.html',
			nextText: ', Dr. Mary Gardner, Veterinary Practice News, Geriatric Pet series, Part II'
		},
		{
			title: 'Increasing Geriatric Pet Visits',
			href: 'https://www.veterinarypracticenews.com/publications/de/201808/index.html',
			nextText: ', Dr. Mary Gardner, Veterinary Practice News, Geriatric Pet series, Part III'
		},
		{
			title: 'Adding Veterinary Hospice Services to Your Practice',
			href: '/veterinary-resources/adding-veterinary-hospice-services.pdf',
			nextText: ', Drs. Mary Gardner and Dani McVety, Today’s Veterinary Practice, Practice-Building series'
		},
		{
			title: 'Caring for Geriatric Pets in Your Practice',
			href: '/veterinary-resources/caring-for-geriatric-pets.pdf',
			nextText: ', Drs. Mary Gardner and Dani McVety, Today’s Veterinary Practice, Practice-Building series'
		},
		{
			title: 'Senior vs. Geriatric: Semantics or Significant?',
			href: 'https://www.dvm360.com/view/senior-vs-geriatric-semantics-or-significant',
			nextText: ', Dr. Mary Gardner, dvm360'
		},
		{
			title: '4 products I love for old pets',
			href: 'https://www.dvm360.com/view/4-products-i-love-old-pets',
			nextText: ', Dr. Mary Gardner, dvm360'
		}
	];

	const LolResources = [
		{
			title: 'Geriatric Questionnaire',
			href: '/veterinary-resources/geriatric-questionnaire.pdf',
			nextText: ''
		}
	];

	const AdditionalResources = [
		{
			title: 'Veterinary Medicine Essentials: Geriatric Medicine',
			href: 'https://www.dvm360.com/view/veterinary-medicine-essentials-geriatric-medicine',
			nextText: ', dvm360'
		},
		{
			title: 'Which Drugs are Used to Treat Cognitive Dysfunction?',
			href: '/veterinary-resources/cognitive-dysfunction-rx-treatment.pdf',
			nextText: ', Plumb’s Therapeutics Brief\n'
		},
		{
			title: '2012 AAFP/ISFM Feline-Friendly Nursing Care Guidelines',
			href: 'https://catvets.com/guidelines/practice-guidelines/nursing-care-guidelines',
			nextText: ''
		},
		{
			title: 'Living With Blind Dogs: A Resource Book and Training Guide for the Owners of Blind and Low-Vision Dogs',
			href: 'https://www.amazon.com/Living-Blind-Dogs-Resource-Low-Vision/dp/0967225345/ref=sr_1_2?dchild=1&keywords=living+with+blind+dogs&qid=1614963104&s=books&sr=1-2',
			nextText: ', Caroline Levin'
		},
		{
			title: 'Dr. Petty’s Pain Relief for Dogs: The Complete Medical and Integrative Guide to Treating Pain',
			href: 'https://www.amazon.com/Dr-Pettys-Pain-Relief-Dogs/dp/158157309X/ref=sr_1_fkmr0_1?ie=UTF8&qid=1454471284&sr=8-1-fkmr0&keywords=mike+petty+pain+relief+dog',
			nextText: ', Michael Petty'
		},
	];

	const GeriatricProducts = [
		{
			title: 'Toe Grips',
			href: 'https://toegrips.com/',
			nextText: '— Helpful for providing traction on hard floors\n'
		},
		{
			title: 'Paw pads',
			href: 'https://www.inthecompanyofdogs.com/itemdy00.aspx?T1=D14208+SMD',
			nextText: '— Self-adhesive paw pads to improve traction on hard floors\n'
		},
		{
			title: 'SleePee Time Bed ',
			href: 'https://www.handicappedpets.com/sleepee-time-bed-for-incontinent-pets/',
			nextText: '— Pet bed that allows urine to drip through so pets stay dry'
		},
		{
			title: 'Help ‘Em Up Harness',
			href: 'https://helpemup.com/',
			nextText: '— Dr. Mary’s favorite harness for supporting weak or injured dogs '
		},
		{
			title: 'Halos for Paws',
			href: 'http://halosforpaws.com/',
			nextText: '— Halos to help blind pets better navigate their environment '
		},
	];

	return (
		<>
			<Helmet>
				<title>Geriatric Clinic Tips | Lap of Love</title>
			</Helmet>
			<div className="resource-item">
				<h4>Tips for Making Your Practice More Geriatric-Friendly</h4>
				<p>
					Geriatric pets make up the largest portion of pets seen in general practice, yet approximately 70% do
					not visit a veterinarian during their last 18 months. Pet owners do not seek care for their geriatric
					pets for many reasons.
				</p>
				<ul>
					<li>Many owners attribute their pet’s medical problems to normal aging changes, and assume nothing can
						be done.
					</li>
					<li>Pet owners aren’t prepared to hear that their pet is old, and that nothing can be done to help.</li>
					<li>Pet owners are not ready to discuss euthanasia.</li>
					<li>Pet owners assume their veterinarian will want to perform costly diagnostics, when all they want is
						their pet’s comfort near the end.
					</li>
				</ul>
				<p>
					By educating and supporting geriatric pet owners, and marketing your services directly to them, you can
					sustain your relationship, and provide the hospice and end-of-life care geriatric pets deserve.
				</p>
				<h4>Make geriatric visits easy for pets and owners</h4>
				<p>
					Encourage more frequent veterinary visits by making navigation of your practice as easy as possible for
					senior and geriatric pets. Designate a close parking space for “grey muzzle” patients, place yoga mats
					on the floor for easier traction, and fast-track geriatric veterinary visits. Handle geriatric patients
					gently, as one wrong move can cause soreness the next day. Also, perform tasks and procedures in the
					exam room to avoid separating geriatric pets from their owners, which can be stressful.
				</p>
				<h4>Provide geriatric resources and education for pet owners</h4>
				<p>
					Similar to the puppy and kitten information you provide, make detailed end-of-life information available
					for owners of geriatric pets, including:
				</p>
				<ul>
					<li>
						Handouts about diseases that most frequently affect geriatric pets
					</li>
					<li>
						Daily diaries that help owners monitor and document their pet’s appetite, thirst, urination,
						defecation, mobility, and disease signs
					</li>
					<li>Quality of life scales</li>
					<li>
						Local adjunctive services, such as acupuncture, massage, mobile grooming, and in-home pet sitting,
						that you support and trust
					</li>
					<li>Local pet loss support groups or grief counselors, including human hospice counselors</li>
					<li>Local emergency hospitals, if your clinic does not offer 24-hour emergency care</li>
					<li>Information on natural death, including specific details</li>
					<li>Specific euthanasia information</li>
				</ul>
				<h4>Resources</h4>
				<h5>
					If you would like additional information about geriatric patient care, you may find the following
					articles, written by Lap of Love’s co-founders, helpful.
				</h5>
				<LinkList list={ResourceListOne} />

				<h5>Lap of Love resources:</h5>
				<LinkList list={LolResources} />

				<h5>Additional resources that may be helpful:</h5>
				<LinkList list={AdditionalResources} />

				<h5>Geriatric and hospice products we recommend:</h5>
				<LinkList list={GeriatricProducts} />
			</div>
		</>
	);
};

export const topics = {
	'hospice-pain-management': {
		title: 'Hospice Pain Management',
		text: 'Effective pain management can mean the difference between a pet suffering on the sidelines and enjoying a good quality of life. With so many options, you can tailor pain management plans to successfully alleviate each patient’s discomfort so they can get back in the game. \n\nLog in to learn more about effective pain management protocols for your patients.\n',
		image: require('@app/content/VetResourceCenter/vet-pain-management.jpg'),
		resource: <PainManagementResource/>
	},
	'euthanasia-sedation-protocols': {
		title: 'Euthanasia Sedation Protocols',
		text: 'The right sedation protocols can help your patients remain calm and relaxed. But, what are the right sedation protocols? Our experienced veterinarians share tried and true protocols for different situations so you can provide your patients the best veterinary experience possible. \n\nLog in to access sedation protocols that will help keep your patients relaxed and stress-free.\n',
		image: require('@app/content/VetResourceCenter/Sedation-protocols-lap-of-love.jpg'),
		resource: <SedationProtocolResource/>
	},
	'euthanasia-best-practices': {
		title: 'Euthanasia Best Practices',
		text: 'Every pet owner wants a peaceful passing for their beloved companion. Our euthanasia best practices, which are based on years of collective knowledge, will help you provide each patient the euthanasia experience they deserve. \n\nLog in to access euthanasia practices that will benefit your patients.\n',
		image: require('@app/content/VetResourceCenter/euthanasia-best-practices.jpg'),
		resource: <BestPracticesResource/>
	},
	'geriatric-clinic-tips': {
		title: 'Geriatric Clinic Tips',
		text: `Provide the loving care your oldest patients deserve by making your practice more geriatric-friendly. From stress-reduction methods to helping patients more easily navigate your layout, we share insights to your geriatric patients’ needs that will help make their visits more comfortable and enjoyable.
\nLog in for tips that will help you address your geriatric patients’ needs.
`,
		image: require('@app/content/VetResourceCenter/geriatric-clinic-tips.jpg'),
		resource: <GeriatricClinicTips />
	}
};

export const sliderItems = [{
	id: 0,
	state: 'OH',
	city: 'Toledo',
	firstName: 'Dr. Sarah Ryan',
	lastName: '',
	message: 'Our Clinic is pleased and proud to partner with Dr. Ryan. Her compassion and genuine love of animals comes through in everything she does with our clients. Dr. Ryan has the unique ability to make an inherently sad situation warm, comfortable and loving. We absolutely love her!',
	avatar: '',
	originalAvatar: '',
	extraText: '~ Dr. Jo Lemanski, Dundee Veterinary Clinic'
}, {
	id: 1,
	state: 'VA',
	city: 'Hampton Roads',
	firstName: 'Dr. Elyse Persico',
	lastName: '',
	message: 'I am so thankful for Dr. Persico and her staff and their services. These are people who will hold your hand through the worst day, hug you as a total stranger and give you every tool possible to get you through it and move you to a place where you can look back and finally say, “That was the right thing to do.” ',
	avatar: '',
	originalAvatar: '',
	extraText: '~ Dr. Erin Drinkwater'
}, {
	id: 2,
	state: 'CA',
	city: 'Silicon Valley',
	firstName: 'Dr. Jeni Goedken',
	lastName: '',
	message: 'I will highly recommend Dr. Jeni Goedken to all pet parents who have to make this hard decision and want to do this in comfort of their own home. Dr. Jeni is an angel who we need in such an emotional hard time, as she will make this difficult time as painless as possible.',
	avatar: '',
	originalAvatar: '',
	extraText: '~ Dr. Raj Singh, Aborn Pet Clinic'
}, {
	id: 3,
	state: 'OK',
	city: 'Oklahoma City',
	firstName: 'Dr. Lisa Rodin',
	lastName: '',
	message: 'Dr. Rodin, thank you so much for taking such good care of our clients in their time of need. We appreciate the service you’ve been able to provide during this strange time.',
	avatar: '',
	originalAvatar: '',
	extraText: '~ Midtown Vets'
}, {
	id: 4,
	state: 'PA',
	city: 'Philadelphia',
	firstName: 'Dr. Brad Bates',
	lastName: '',
	message: 'I’ve heard wonderful things about you from my clients for so many years, and it was good to meet you in person, even under emotional circumstances. I can’t tell you how grateful I am for the work you do, and I’m so grateful to be able to refer my clients to you.',
	avatar: '',
	originalAvatar: '',
	extraText: '~ Dr. Natasha Kassell, Holistic House Call Veterinarian'
}, {
	id: 5,
	state: 'PA',
	city: 'Philadelphia',
	firstName: 'Dr. Brad Bates',
	lastName: '',
	message: 'Again, thank you for doing what you do.  I know it must be very difficult, but your service is greatly appreciated.',
	avatar: '',
	originalAvatar: '',
	extraText: '~ Dr. Caroline Lafferty, Greenfields Veterinary Associates'
}, {
	id: 6,
	state: 'TX',
	city: 'Brazos Valley',
	firstName: 'Dr. Colleen Lambo',
	lastName: '',
	message: 'We are so grateful to have Dr. Lambo for after-hours end of life care; she’s an amazing person, kind hearted, compassionate veterinarian. I would not hesitate to contact her for end of life care for my own pets because we all know it’s one of the hardest decisions to make but it can be made so much better with someone who compassionately guides them.',
	avatar: '',
	originalAvatar: '',
	extraText: '~ Dr. Brittany Marvel, Chasing Tails Mobile Vet Service'
}, {
	id: 7,
	state: 'TX',
	city: 'Brazos Valley',
	firstName: 'Dr. Colleen Lambo',
	lastName: '',
	message: 'Dr. Colleen turned a situation that I had dreaded into a positive experience I will never forget. She made the end of Wilson’s life into such a positive experience for me and my wife.',
	avatar: '',
	originalAvatar: '',
	extraText: '~ Dr. Jeff Edwards, Veterinary Pathologist'
}, {
	id: 8,
	state: 'OH',
	city: 'Columbus',
	firstName: 'Dr. Nellie Wilbers',
	lastName: '',
	message: 'I continue to recommend you as first choice for euthanasia if I am not available. Every single person who has given feedback says you are terrific. I am grateful to be able to refer even my favorite families to you with confidence.',
	avatar: '',
	originalAvatar: '',
	extraText: '~ Dr. Jennifer Taylor Partridge, Housecalls for Dogs & Cats'
}, {
	id: 9,
	state: 'OH',
	city: 'Columbus',
	firstName: 'Dr. Nellie Wilbers',
	lastName: '',
	message: 'I think what you have to offer clients is a wonderful and beautiful thing. Thank you for doing what you do!',
	avatar: '',
	originalAvatar: '',
	extraText: '~ Dani Ventola, Office Manager, RVT, Stringtown Animal Hospital'
}, {
	id: 10,
	state: 'OH',
	city: 'Columbus',
	firstName: 'Dr. Nellie Wilbers',
	lastName: '',
	message: 'I wanted to thank all at Lap of Love for the way they offer their services in such a compassionate manner. The Blackburns were greatly impressed with the care that was given them and their dog during this stressful time. They specifically wanted to shout out to Dr. Nellie Wilbers for the empathy that she displayed. As a DVM, I also appreciated that you were able to provide such a needed service for clients in distress in their homes. Thanks for what you do. ',
	avatar: '',
	originalAvatar: '',
	extraText: '~ Dr. Dennis Chew'
}];